<template>
    <div v-if="html" class="feedback-content">
        <div ref="content" class="feedback-prose" v-html="html" @click="openImage" @keydown="imageKey"></div>
        <dialog ref="viewer" class="feedback-viewer" aria-label="Visor de imagen" @click="backdrop" @cancel.prevent="closeViewer" @close="reset">
            <div class="viewer-toolbar">
                <span>Imagen</span>
                <button type="button" aria-label="Reducir imagen" :disabled="zoom <= 1" @click="setZoom(zoom - 0.5)">−</button>
                <button type="button" aria-label="Restablecer zoom" @click="reset">{{ Math.round(zoom * 100) }} %</button>
                <button type="button" aria-label="Ampliar imagen" :disabled="zoom >= 4" @click="setZoom(zoom + 0.5)">+</button>
                <button type="button" autofocus aria-label="Cerrar visor" @click="closeViewer">Cerrar ×</button>
            </div>
            <div ref="viewport" class="viewer-viewport" @pointerdown="pointerDown" @pointermove="pointerMove"
                @pointerup="pointerEnd" @pointercancel="pointerEnd" @lostpointercapture="pointerEnd" @dblclick="setZoom(zoom === 1 ? 2 : 1)">
                <img :src="imageSrc" :alt="imageAlt" draggable="false" :style="{ transform: `translate(${panX}px, ${panY}px) scale(${zoom})` }">
            </div>
            <p class="viewer-hint">Usa + y − para ampliar. Arrastra o pellizca con dos dedos.</p>
        </dialog>
    </div>
</template>

<script>
import { sanitizeFeedback } from '@/utils/feedback-html';

export default {
    props: { value: { type: String, default: '' } },
    data: () => ({ imageSrc: '', imageAlt: '', zoom: 1, panX: 0, panY: 0, pointers: new Map() }),
    computed: { html() { return sanitizeFeedback(this.value); } },
    watch: { html() { this.closeViewer(); this.$nextTick(this.prepareImages); } },
    mounted() { this.prepareImages(); },
    beforeUnmount() { this.$refs.viewer?.close(); },
    methods: {
        prepareImages() {
            this.$refs.content?.querySelectorAll('img').forEach(image => {
                image.tabIndex = 0;
                image.setAttribute('role', 'button');
                image.setAttribute('aria-label', `Ampliar: ${image.alt}`);
            });
        },
        openImage(event) {
            if (event.target.tagName !== 'IMG') return;
            this.imageSrc = event.target.src;
            this.imageAlt = event.target.alt;
            this.reset();
            this.$refs.viewer.showModal();
        },
        imageKey(event) {
            if (event.target.tagName === 'IMG' && ['Enter', ' '].includes(event.key)) {
                event.preventDefault();
                this.openImage(event);
            }
        },
        closeViewer() { this.$refs.viewer?.close(); this.reset(); },
        backdrop(event) { if (event.target === this.$refs.viewer) this.closeViewer(); },
        reset() { this.zoom = 1; this.panX = 0; this.panY = 0; this.pointers.clear(); },
        setZoom(value) {
            this.zoom = Math.min(4, Math.max(1, value));
            this.clampPan();
        },
        clampPan() {
            const viewport = this.$refs.viewport;
            const img = viewport?.querySelector('img');
            const maxX = Math.max(0, ((img?.clientWidth || 0) * this.zoom - (viewport?.clientWidth || 0)) / 2);
            const maxY = Math.max(0, ((img?.clientHeight || 0) * this.zoom - (viewport?.clientHeight || 0)) / 2);
            this.panX = Math.max(-maxX, Math.min(maxX, this.panX));
            this.panY = Math.max(-maxY, Math.min(maxY, this.panY));
        },
        pointerDown(event) {
            if (event.pointerType === 'mouse' && event.button !== 0) return;
            this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            event.currentTarget.setPointerCapture(event.pointerId);
        },
        pointerMove(event) {
            const previous = this.pointers.get(event.pointerId);
            if (!previous) return;
            const distance = () => {
                const [a, b] = [...this.pointers.values()];
                return b ? Math.hypot(a.x - b.x, a.y - b.y) : 0;
            };
            const before = distance();
            this.pointers.set(event.pointerId, { x: event.clientX, y: event.clientY });
            if (this.pointers.size === 2 && before > 0) this.setZoom(this.zoom * distance() / before);
            else if (this.pointers.size === 1 && this.zoom > 1) {
                this.panX += event.clientX - previous.x;
                this.panY += event.clientY - previous.y;
                this.clampPan();
            }
        },
        pointerEnd(event) { this.pointers.delete(event.pointerId); },
    },
};
</script>

<style scoped>
.feedback-prose { overflow-wrap: anywhere; line-height: 1.6; white-space: pre-wrap; }
.feedback-prose :deep(img) { display: block; max-width: 100%; max-height: 360px; height: auto; margin: 1rem 0; border-radius: 8px; cursor: zoom-in; }
.feedback-prose :deep(img:focus-visible) { outline: 3px solid #2373b9; outline-offset: 4px; }
.feedback-prose :deep(img[width]) { max-height: none; }
.feedback-prose :deep(img[data-align=left]) { margin-left: 0; margin-right: auto; }
.feedback-prose :deep(img[data-align=center]) { margin-left: auto; margin-right: auto; }
.feedback-prose :deep(img[data-align=right]) { margin-left: auto; margin-right: 0; }
.feedback-prose :deep(a) { color: #1263a1; text-decoration: underline; }
.feedback-prose :deep([data-marquee]) { overflow: hidden; }
.feedback-prose :deep([data-marquee] > [data-marquee-text]) { display: inline-block; max-width: 100%; animation: feedback-marquee 8s ease-in-out infinite alternate; }
.feedback-prose :deep([data-marquee]:hover > [data-marquee-text]), .feedback-prose :deep([data-marquee]:focus-within > [data-marquee-text]) { animation-play-state: paused; }
@keyframes feedback-marquee { from { transform: translateX(0); } to { transform: translateX(20%); } }
@media (prefers-reduced-motion: reduce) { .feedback-prose :deep([data-marquee] > [data-marquee-text]) { animation: none; } }
.feedback-prose :deep(blockquote) { border-left: 3px solid currentColor; padding-left: 1rem; }
.feedback-viewer { width: 96vw; max-width: 1400px; height: 92dvh; max-height: 96dvh; padding: 0; border: 0; border-radius: 12px; background: #111827; color: #fff; overflow: hidden; }
.feedback-viewer[open] { display: flex; flex-direction: column; }
.feedback-viewer::backdrop { background: #000c; }
.viewer-toolbar { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; padding: 12px; }
.viewer-toolbar span { margin-right: auto; }
.viewer-toolbar button { min-width: 44px; min-height: 44px; border: 1px solid #64748b; border-radius: 8px; background: #1e293b; color: white; padding: 6px 12px; touch-action: manipulation; }
.viewer-toolbar button:disabled { opacity: 0.4; }
.viewer-viewport { flex: 1; min-height: 0; display: flex; align-items: center; justify-content: center; overflow: hidden; touch-action: none; user-select: none; cursor: grab; }
.viewer-viewport img { max-width: 100%; max-height: 100%; object-fit: contain; pointer-events: none; }
.viewer-hint { text-align: center; font-size: 0.8rem; padding: 8px; margin: 0; }
@media (max-width: 480px) { .viewer-toolbar { gap: 4px; padding: 8px; } .viewer-toolbar span { display: none; } }
</style>
